import { useMemo, useState } from "react";
import { useParams, Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAiAgentsSurface } from "@/pages/ai-agents-surface";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  Rocket,
  Pause,
  Play,
  Ban,
  Loader2,
  AlertTriangle,
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
  ListOrdered,
} from "lucide-react";
import type { AiAgent, AiAgentRun } from "@shared/schema";
import { LeadOutreachWizard } from "@/pages/platform/lead-outreach-wizard";
import { RunQueueBuilder } from "@/pages/platform/run-queue-builder";

// Projected funnel the server recomputes from each run's stored scope so the
// dashboard can reconcile estimate-vs-actual.
type RunEstimate = {
  prospects: number;
  qualified: number;
  enrolled: number;
  estimatedCostUsd: number;
};
type RunRow = AiAgentRun & { estimate?: RunEstimate };
type AgentWithRuns = AiAgent & { runs: RunRow[] };

const RUN_STATUS_STYLES: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  estimating: "bg-muted text-muted-foreground",
  queued: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  running: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  paused: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  completed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  cancelled: "bg-muted text-muted-foreground",
};

const ACTIVE_STATUSES = new Set(["queued", "running"]);

function usd(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}
function num(n: number): string {
  return n.toLocaleString("en-US");
}

export default function PlatformAiAgentRuns() {
  const { agentId } = useParams<{ agentId: string }>();
  const { toast } = useToast();
  const { apiBase: AI_AGENTS_API, routeBase, canLaunch, Chrome } =
    useAiAgentsSurface();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [queueBuilderOpen, setQueueBuilderOpen] = useState(false);
  const [launchTarget, setLaunchTarget] = useState<AiAgentRun | null>(null);
  const [launchCap, setLaunchCap] = useState("");
  // When set, the cap dialog is raising the cap to resume an auto-paused run
  // rather than launching a draft.
  const [launchMode, setLaunchMode] = useState<"launch" | "resume">("launch");

  const { data: agent, isLoading } = useQuery<AgentWithRuns>({
    queryKey: [AI_AGENTS_API, agentId],
    queryFn: async () => {
      const res = await apiRequest("GET", `${AI_AGENTS_API}/${agentId}`);
      return res.json();
    },
    enabled: !!agentId,
    // Poll while any run is mid-flight so progress/spend/funnel stay live.
    refetchInterval: (q) => {
      const data = q.state.data as AgentWithRuns | undefined;
      const anyActive = data?.runs?.some((r) => ACTIVE_STATUSES.has(r.status));
      return anyActive ? 4000 : false;
    },
  });

  const runs = useMemo(
    () =>
      [...(agent?.runs ?? [])].sort(
        (a, b) =>
          new Date(b.createdAt as any).getTime() - new Date(a.createdAt as any).getTime(),
      ),
    [agent?.runs],
  );

  // Group batched runs (those with a batchId) into queues, ordered by their
  // queue position. Each entry is [batchId, runs-in-order]. Solo runs (no
  // batchId) are excluded — they render as standalone cards below.
  const batches = useMemo(() => {
    const map = new Map<string, RunRow[]>();
    for (const r of runs) {
      if (!r.batchId) continue;
      const arr = map.get(r.batchId) ?? [];
      arr.push(r);
      map.set(r.batchId, arr);
    }
    for (const arr of map.values())
      arr.sort((a, b) => (a.queuePosition ?? 0) - (b.queuePosition ?? 0));
    // Newest queues first (by the earliest-created run in each batch).
    return [...map.entries()].sort((a, b) => {
      const at = Math.min(...a[1].map((r) => new Date(r.createdAt as any).getTime()));
      const bt = Math.min(...b[1].map((r) => new Date(r.createdAt as any).getTime()));
      return bt - at;
    });
  }, [runs]);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: [AI_AGENTS_API, agentId] });
    queryClient.invalidateQueries({ queryKey: [AI_AGENTS_API] });
  }

  const controlMutation = useMutation({
    mutationFn: async ({ runId, action }: { runId: string; action: "pause" | "resume" | "cancel" }) => {
      const res = await apiRequest("POST", `${AI_AGENTS_API}/runs/${runId}/${action}`);
      return res.json();
    },
    onSuccess: (_d, v) => {
      invalidate();
      toast({ title: `Run ${v.action === "cancel" ? "cancelled" : v.action + "d"}` });
    },
    onError: (e: any) =>
      toast({ title: "Action failed", description: e?.message, variant: "destructive" }),
  });

  const capMutation = useMutation({
    mutationFn: async ({ runId, cap, mode }: { runId: string; cap: number; mode: "launch" | "resume" }) => {
      const res = await apiRequest("POST", `${AI_AGENTS_API}/runs/${runId}/${mode}`, {
        spendCapUsd: cap,
      });
      return res.json();
    },
    onSuccess: (_d, v) => {
      invalidate();
      setLaunchTarget(null);
      setLaunchCap("");
      toast({
        title: v.mode === "resume" ? "Run resumed" : "Run launched",
        description: "It auto-pauses the instant real spend crosses the cap.",
      });
    },
    onError: (e: any) =>
      toast({ title: "Could not start run", description: e?.message, variant: "destructive" }),
  });

  const cancelBatchMutation = useMutation({
    mutationFn: async (batchId: string) => {
      const res = await apiRequest("POST", `${AI_AGENTS_API}/batches/${batchId}/cancel`);
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      toast({
        title: "Run queue cancelled",
        description: "Pending runs were cancelled; any active run finishes its current step.",
      });
    },
    onError: (e: any) =>
      toast({ title: "Could not cancel queue", description: e?.message, variant: "destructive" }),
  });

  const capValue = Number(launchCap);
  const capValid = launchCap.trim() !== "" && Number.isFinite(capValue) && capValue > 0;

  return (
    <Chrome>
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div>
          <Link href={routeBase}>
            <Button variant="ghost" size="sm" className="-ml-2 mb-2" data-testid="link-back-agents">
              <ArrowLeft className="h-4 w-4 mr-1" /> All agents
            </Button>
          </Link>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-agent-name">
                {agent?.name ?? "Agent"} — Runs
              </h1>
              <p className="text-sm text-muted-foreground max-w-2xl">
                Track each Lead Outreach run end to end: the real net-and-sift funnel,
                live spend against the cap, and true cost per lead.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setQueueBuilderOpen(true)}
                disabled={!agent || agent.status !== "active"}
                data-testid="button-new-queue"
              >
                <ListOrdered className="h-4 w-4 mr-2" /> New run queue
              </Button>
              <Button
                onClick={() => setWizardOpen(true)}
                disabled={!agent || agent.status !== "active"}
                data-testid="button-new-run"
              >
                <Rocket className="h-4 w-4 mr-2" /> New run
              </Button>
            </div>
          </div>
        </div>

        {/* Run-queue progress panels */}
        {batches.length > 0 && (
          <div className="space-y-3" data-testid="section-run-queues">
            {batches.map(([batchId, batchRuns]) => (
              <BatchProgressPanel
                key={batchId}
                batchId={batchId}
                runs={batchRuns}
                onCancel={() => cancelBatchMutation.mutate(batchId)}
                busy={cancelBatchMutation.isPending}
              />
            ))}
          </div>
        )}

        {isLoading ? (
          <div className="space-y-4">
            {[0, 1].map((i) => (
              <div key={i} className="h-48 rounded-lg border bg-muted/30 animate-pulse" />
            ))}
          </div>
        ) : runs.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Activity className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium">No runs yet</h3>
              <p className="text-sm text-muted-foreground max-w-sm mt-1">
                Start a Lead Outreach run to cast a wide net of prospects and sift it
                down to a small, deliverable, qualified subset.
              </p>
              <Button className="mt-4" onClick={() => setWizardOpen(true)} disabled={agent?.status !== "active"} data-testid="button-new-run-empty">
                <Rocket className="h-4 w-4 mr-2" /> New run
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {runs.map((run) => (
              <RunCard
                key={run.id}
                run={run}
                canLaunch={canLaunch}
                onPause={() => controlMutation.mutate({ runId: run.id, action: "pause" })}
                onResume={() => {
                  // A cap-paused run needs a higher cap to make progress, so
                  // route it through the cap dialog. A manually paused run
                  // resumes directly under its existing cap.
                  if (run.pausedReason === "spend_cap_reached") {
                    setLaunchMode("resume");
                    setLaunchTarget(run);
                    setLaunchCap("");
                  } else {
                    controlMutation.mutate({ runId: run.id, action: "resume" });
                  }
                }}
                onCancel={() => controlMutation.mutate({ runId: run.id, action: "cancel" })}
                onLaunch={() => {
                  setLaunchMode("launch");
                  setLaunchTarget(run);
                  setLaunchCap("");
                }}
                busy={controlMutation.isPending}
              />
            ))}
          </div>
        )}
      </div>

      {/* New-run wizard */}
      {agent && (
        <LeadOutreachWizard
          agent={agent}
          open={wizardOpen}
          onOpenChange={(o) => {
            setWizardOpen(o);
            if (!o) invalidate();
          }}
        />
      )}

      {/* New run-queue builder */}
      {agent && (
        <RunQueueBuilder
          agent={agent}
          open={queueBuilderOpen}
          onOpenChange={(o) => {
            setQueueBuilderOpen(o);
            if (!o) invalidate();
          }}
        />
      )}

      {/* Spend-cap dialog — launch a draft OR raise the cap to resume a
          run that auto-paused at its cap. */}
      <Dialog open={!!launchTarget} onOpenChange={(o) => !o && setLaunchTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {launchMode === "resume" ? "Raise cap & resume" : "Launch this run"}
            </DialogTitle>
            <DialogDescription>
              {launchMode === "resume"
                ? "This run paused when it reached its spend cap. Enter a higher cap to keep going — it must exceed what was already spent."
                : "Set a per-run spend cap. There is no default — the run auto-pauses the instant real provider spend crosses it. You can resume later with a higher cap."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="launch-cap">Per-run spend cap (USD)</Label>
            <Input
              id="launch-cap"
              type="number"
              min="1"
              step="1"
              inputMode="decimal"
              placeholder="e.g. 20"
              value={launchCap}
              onChange={(e) => setLaunchCap(e.target.value)}
              data-testid="input-launch-cap"
            />
            {launchMode === "resume" && launchTarget && (
              <p className="text-xs text-muted-foreground" data-testid="text-already-spent">
                Already spent: {usd(Number(launchTarget.actualCostUsd) || 0)}
              </p>
            )}
            {launchMode === "resume" &&
              launchTarget &&
              capValid &&
              capValue <= (Number(launchTarget.actualCostUsd) || 0) && (
                <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>Cap must be greater than what was already spent.</span>
                </div>
              )}
            {launchMode === "launch" &&
              launchTarget?.estimatedCostUsd != null &&
              capValid &&
              capValue < Number(launchTarget.estimatedCostUsd) && (
                <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>
                    Below the projected cost ({usd(Number(launchTarget.estimatedCostUsd))}).
                    The run will likely pause before covering the full scope.
                  </span>
                </div>
              )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLaunchTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                launchTarget &&
                capMutation.mutate({ runId: launchTarget.id, cap: capValue, mode: launchMode })
              }
              disabled={
                !capValid ||
                capMutation.isPending ||
                (launchMode === "resume" &&
                  !!launchTarget &&
                  capValue <= (Number(launchTarget.actualCostUsd) || 0))
              }
              data-testid="button-confirm-launch"
            >
              {launchMode === "resume" ? <Play className="h-4 w-4 mr-2" /> : <Rocket className="h-4 w-4 mr-2" />}
              {capMutation.isPending
                ? launchMode === "resume"
                  ? "Resuming…"
                  : "Launching…"
                : launchMode === "resume"
                  ? "Raise cap & resume"
                  : "Launch run"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Chrome>
  );
}

// Live progress for one run queue (batch). Derives batch-level state from its
// runs' statuses so it stays in sync with the same 4s poll that drives the run
// cards — no separate batch fetch needed. Per-run tracking lives in the cards
// below; this panel summarizes the queue (active / queued / done + spend).
function BatchProgressPanel({
  batchId,
  runs,
  onCancel,
  busy,
}: {
  batchId: string;
  runs: RunRow[];
  onCancel: () => void;
  busy: boolean;
}) {
  const terminal = new Set(["completed", "cancelled", "failed"]);
  const done = runs.filter((r) => terminal.has(r.status)).length;
  const active = runs.find((r) => ACTIVE_STATUSES.has(r.status));
  const paused = runs.filter((r) => r.status === "paused").length;
  const hasActive = runs.some(
    (r) => ACTIVE_STATUSES.has(r.status) || r.status === "paused" || r.status === "draft" || r.status === "estimating",
  );
  const totalSpend = runs.reduce((n, r) => n + (Number(r.actualCostUsd) || 0), 0);
  const pct = runs.length > 0 ? Math.round((done / runs.length) * 100) : 0;

  const batchStatus = hasActive
    ? active
      ? "running"
      : paused > 0
        ? "paused"
        : "queued"
    : "complete";
  const statusStyle =
    batchStatus === "running"
      ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
      : batchStatus === "paused"
        ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
        : batchStatus === "complete"
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
          : "bg-muted text-muted-foreground";

  return (
    <Card data-testid={`card-batch-${batchId}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <ListOrdered className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Run queue</CardTitle>
            <Badge variant="secondary" className={statusStyle} data-testid={`status-batch-${batchId}`}>
              {batchStatus === "running" && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              {batchStatus}
            </Badge>
            <span className="text-xs text-muted-foreground" data-testid={`text-batch-progress-${batchId}`}>
              {num(done)} / {num(runs.length)} runs done
            </span>
          </div>
          {hasActive && (
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={onCancel}
              disabled={busy}
              data-testid={`button-cancel-batch-${batchId}`}
            >
              <Ban className="h-4 w-4 mr-1" /> Cancel queue
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Queue progress</span>
            <span>{pct}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded bg-muted">
            <div className="h-full bg-blue-500" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {runs.map((r, i) => (
            <span
              key={r.id}
              className={`flex items-center gap-1 rounded px-2 py-1 text-xs ${RUN_STATUS_STYLES[r.status] ?? "bg-muted"}`}
              title={r.name || `Run ${i + 1}`}
              data-testid={`batch-run-chip-${r.id}`}
            >
              {ACTIVE_STATUSES.has(r.status) && <Loader2 className="h-3 w-3 animate-spin" />}
              #{(r.queuePosition ?? i) + 1} {r.status}
            </span>
          ))}
        </div>

        <div className="text-xs text-muted-foreground" data-testid={`text-batch-spend-${batchId}`}>
          Total queue spend so far: {usd(totalSpend)}
        </div>
      </CardContent>
    </Card>
  );
}

function RunCard({
  run,
  canLaunch,
  onPause,
  onResume,
  onCancel,
  onLaunch,
  busy,
}: {
  run: RunRow;
  canLaunch: boolean;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  onLaunch: () => void;
  busy: boolean;
}) {
  const cap = run.spendCapUsd != null ? Number(run.spendCapUsd) : null;
  const spent = Number(run.actualCostUsd) || 0;
  const estCost = run.estimatedCostUsd != null ? Number(run.estimatedCostUsd) : null;
  const spendPct = cap && cap > 0 ? Math.min(100, Math.round((spent / cap) * 100)) : 0;

  const searched = run.searchedCount;
  const imported = run.importedCount;
  const enrolled = run.enrolledCount;
  const suppressed = run.suppressedCount;
  const failed = run.failedCount;
  const funnelMax = Math.max(searched, imported, enrolled, 1);

  const progressPct =
    run.totalItems > 0 ? Math.round((run.completedItems / run.totalItems) * 100) : 0;

  const isActive = ACTIVE_STATUSES.has(run.status);
  const isDraft = run.status === "draft" || run.status === "estimating";
  const isPaused = run.status === "paused";
  const isTerminal = ["completed", "cancelled", "failed"].includes(run.status);
  const cappedPause = isPaused && run.pausedReason === "spend_cap_reached";
  const confirmPause = isPaused && run.pausedReason === "confirm_threshold";

  const costPerImported = imported > 0 ? spent / imported : null;
  const costPerEnrolled = enrolled > 0 ? spent / enrolled : null;

  return (
    <Card data-testid={`card-run-${run.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <CardTitle className="text-base" data-testid={`text-run-name-${run.id}`}>
              {run.name || "Untitled run"}
            </CardTitle>
            <Badge variant="secondary" className={RUN_STATUS_STYLES[run.status] ?? ""} data-testid={`status-run-${run.id}`}>
              {isActive && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              {run.status}
            </Badge>
            {run.campaignType && (
              <span className="text-xs text-muted-foreground">
                {run.campaignType === "drip" ? "Cold drip" : "Mass send"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isDraft && canLaunch && (
              <Button size="sm" onClick={onLaunch} data-testid={`button-launch-${run.id}`}>
                <Rocket className="h-4 w-4 mr-1" /> Launch
              </Button>
            )}
            {isDraft && !canLaunch && (
              <span
                className="text-xs text-muted-foreground"
                data-testid={`text-draft-launch-disabled-${run.id}`}
              >
                Draft saved — sending not enabled yet
              </span>
            )}
            {isActive && (
              <Button size="sm" variant="outline" onClick={onPause} disabled={busy} data-testid={`button-pause-run-${run.id}`}>
                <Pause className="h-4 w-4 mr-1" /> Pause
              </Button>
            )}
            {isPaused && canLaunch && (
              <Button size="sm" variant="outline" onClick={onResume} disabled={busy} data-testid={`button-resume-run-${run.id}`}>
                <Play className="h-4 w-4 mr-1" /> Resume
              </Button>
            )}
            {!isTerminal && !isDraft && (
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={onCancel} disabled={busy} data-testid={`button-cancel-run-${run.id}`}>
                <Ban className="h-4 w-4 mr-1" /> Cancel
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {cappedPause && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200" data-testid={`notice-capped-${run.id}`}>
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Auto-paused — spend reached the cap. Resume with a higher cap to continue.</span>
          </div>
        )}

        {confirmPause && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200" data-testid={`notice-confirm-${run.id}`}>
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Checking in — this run has spent{" "}
              <span className="font-semibold">
                {Math.round(Number(run.actualCredits) || 0).toLocaleString()} credits
              </span>{" "}
              so far. Resume to approve the next 400, or cancel to stop here.
            </span>
          </div>
        )}

        {/* Progress (cells processed) */}
        {(isActive || isPaused) && run.totalItems > 0 && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Searches processed</span>
              <span data-testid={`text-progress-${run.id}`}>
                {num(run.completedItems)} / {num(run.totalItems)} ({progressPct}%)
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded bg-muted">
              <div className="h-full bg-blue-500" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}

        {/* Spend vs cap */}
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Spend</span>
            <span data-testid={`text-spend-${run.id}`}>
              {usd(spent)}{cap != null ? ` of ${usd(cap)} cap` : ""}
            </span>
          </div>
          {cap != null && (
            <div className="h-2 overflow-hidden rounded bg-muted">
              <div
                className={`h-full ${spendPct >= 100 ? "bg-amber-500" : "bg-emerald-500"}`}
                style={{ width: `${spendPct}%` }}
              />
            </div>
          )}
        </div>

        {/* Actual funnel */}
        <div className="space-y-1.5" data-testid={`funnel-run-${run.id}`}>
          <FunnelRow label="Searched (net cast)" value={searched} max={funnelMax} tone="bg-blue-500" testid={`searched-${run.id}`} />
          <FunnelRow label="Imported (sifted & deliverable)" value={imported} max={funnelMax} tone="bg-emerald-500" testid={`imported-${run.id}`} />
          <FunnelRow label="Enrolled in outreach" value={enrolled} max={funnelMax} tone="bg-emerald-600" testid={`enrolled-${run.id}`} />
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          <span className="rounded bg-muted px-2 py-1" data-testid={`chip-suppressed-${run.id}`}>
            {num(suppressed)} suppressed (undeliverable)
          </span>
          {failed > 0 && (
            <span className="rounded bg-red-100 px-2 py-1 text-red-700 dark:bg-red-900/40 dark:text-red-300" data-testid={`chip-failed-${run.id}`}>
              {num(failed)} failed searches
            </span>
          )}
          {enrolled === 0 && (imported > 0 || isTerminal) && (
            <span className="rounded bg-muted px-2 py-1 text-muted-foreground">
              Cold enrollment deferred (no verified cold-send domain wired yet)
            </span>
          )}
        </div>

        {/* ROI */}
        <div className="grid grid-cols-3 gap-3 pt-1">
          <Stat label="True cost" value={usd(spent)} sub="our provider spend" testid={`roi-cost-${run.id}`} />
          <Stat
            label="Cost / imported"
            value={costPerImported != null ? usd(costPerImported) : "—"}
            sub="qualified lead"
            testid={`roi-cpi-${run.id}`}
          />
          <Stat
            label="Cost / enrolled"
            value={costPerEnrolled != null ? usd(costPerEnrolled) : "—"}
            sub={estCost != null ? `est. ${usd(estCost)} total` : "actual"}
            testid={`roi-cpe-${run.id}`}
          />
        </div>

        {/* Estimate vs actual reconciliation — only once the run has launched
            (a draft has no actuals to reconcile against). */}
        {!isDraft && <Reconciliation run={run} />}
      </CardContent>
    </Card>
  );
}

function pctDelta(est: number, act: number): number | null {
  if (!Number.isFinite(est) || est === 0) return null;
  return ((act - est) / est) * 100;
}

function deltaTone(delta: number | null, good: "lower" | "neutral"): string {
  if (delta == null || Math.abs(delta) < 0.5) return "text-muted-foreground";
  const favorable = good === "lower" ? delta < 0 : delta > 0;
  return favorable
    ? "text-emerald-600 dark:text-emerald-400"
    : "text-amber-600 dark:text-amber-400";
}

function Reconciliation({ run }: { run: RunRow }) {
  const est = run.estimate;
  if (!est) return null;

  // The persisted anchors win where they exist; the recomputed funnel fills in
  // the projected "qualified" stage that was never persisted.
  const estSearched =
    run.estimatedProspects != null ? Number(run.estimatedProspects) : est.prospects;
  const estImported = est.qualified;
  const estCost =
    run.estimatedCostUsd != null ? Number(run.estimatedCostUsd) : est.estimatedCostUsd;

  const actSearched = run.searchedCount;
  const actImported = run.importedCount;
  const actCost = Number(run.actualCostUsd) || 0;

  const estCpl = estImported > 0 ? estCost / estImported : null;
  const actCpl = actImported > 0 ? actCost / actImported : null;

  return (
    <div
      className="rounded-md border bg-muted/20 p-3 space-y-2"
      data-testid={`recon-run-${run.id}`}
    >
      <div className="text-xs font-medium text-muted-foreground">
        Estimate vs actual
      </div>
      <ReconRow
        label="Net searched"
        est={num(estSearched)}
        act={num(actSearched)}
        delta={pctDelta(estSearched, actSearched)}
        good="neutral"
        testid={`recon-searched-${run.id}`}
      />
      <ReconRow
        label="Qualified imported"
        est={num(estImported)}
        act={num(actImported)}
        delta={pctDelta(estImported, actImported)}
        good="neutral"
        testid={`recon-imported-${run.id}`}
      />
      <ReconRow
        label="Spend"
        est={usd(estCost)}
        act={usd(actCost)}
        delta={pctDelta(estCost, actCost)}
        good="lower"
        testid={`recon-spend-${run.id}`}
      />
      <ReconRow
        label="Cost / qualified"
        est={estCpl != null ? usd(estCpl) : "—"}
        act={actCpl != null ? usd(actCpl) : "—"}
        delta={estCpl != null && actCpl != null ? pctDelta(estCpl, actCpl) : null}
        good="lower"
        testid={`recon-cpl-${run.id}`}
      />
    </div>
  );
}

function ReconRow({
  label,
  est,
  act,
  delta,
  good,
  testid,
}: {
  label: string;
  est: string;
  act: string;
  delta: number | null;
  good: "lower" | "neutral";
  testid: string;
}) {
  const tone = deltaTone(delta, good);
  const flat = delta == null || Math.abs(delta) < 0.5;
  const Arrow = flat ? Minus : delta! > 0 ? TrendingUp : TrendingDown;
  return (
    <div className="flex items-center justify-between gap-3 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">est {est}</span>
        <span className="text-muted-foreground">→</span>
        <span className="font-medium text-foreground" data-testid={testid}>
          {act}
        </span>
        <span
          className={`inline-flex w-14 items-center justify-end gap-0.5 tabular-nums ${tone}`}
          data-testid={`${testid}-delta`}
        >
          <Arrow className="h-3 w-3" />
          {delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta.toFixed(0)}%`}
        </span>
      </div>
    </div>
  );
}

function FunnelRow({
  label,
  value,
  max,
  tone,
  testid,
}: {
  label: string;
  value: number;
  max: number;
  tone: string;
  testid: string;
}) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="w-52 shrink-0 text-xs text-muted-foreground">{label}</span>
      <div className="relative h-6 flex-1 overflow-hidden rounded bg-muted">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
        <span className="absolute inset-y-0 left-2 flex items-center text-xs font-medium text-foreground" data-testid={`funnel-${testid}`}>
          {value.toLocaleString("en-US")}
        </span>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  testid,
}: {
  label: string;
  value: string;
  sub: string;
  testid: string;
}) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold" data-testid={testid}>{value}</div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </div>
  );
}
